import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { advertisementService } from '../../services/advertisementService';
import apiService from '../../services/api';

const AdvertisementsManagement = () => {
  const { t } = useTranslation();
  const [advertisements, setAdvertisements] = useState([]);
  const [serviceProviders, setServiceProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedAdvertisement, setSelectedAdvertisement] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [designDialog, setDesignDialog] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [designForm, setDesignForm] = useState({
    design_name: '',
    design_description: '',
    design_cost: 0,
    design: null,
  });
  const [advertisementForm, setAdvertisementForm] = useState({
    name: '',
    description: '',
    type: 'ready_image',
    image: null,
    design_requirements: '',
    user_id: '',
  });
  const [actionLoading, setActionLoading] = useState(false);

  const tabs = [
    { label: t('AllAdvertisements'), value: 'all' },
    { label: t('PendingAdvertisements'), value: 'pending' },
    { label: t('ApprovedAdvertisements'), value: 'approved' },
    { label: t('ActiveAdvertisements'), value: 'active' },
    { label: t('RejectedAdvertisements'), value: 'rejected' },
  ];

  useEffect(() => {
    loadAdvertisements();
    loadServiceProviders();
  }, []);

  const loadAdvertisements = async () => {
    try {
      setLoading(true);
      const response = await advertisementService.getAllAdvertisements();
      
      if (response.success) {
        setAdvertisements(response.data);
      } else {
        console.error('Failed to load advertisements:', response.message);
      }
    } catch (error) {
      console.error('Error loading advertisements:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadServiceProviders = async () => {
    try {
      const response = await apiService.getProfiles();
      if (response.success) {
        // Filter only service providers
        const providers = response.data.filter(user => 
          user.role === 'ServiceProvider' || user.role === 'serviceprovider'
        );
        setServiceProviders(providers);
      }
    } catch (error) {
      console.error('Error loading service providers:', error);
    }
  };

  const getFilteredAdvertisements = () => {
    if (selectedTab === 0) return advertisements;
    return advertisements.filter(ad => ad.status === tabs[selectedTab].value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'active':
        return 'bg-blue-100 text-blue-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return t('PendingAdvertisements');
      case 'approved':
        return t('ApprovedAdvertisements');
      case 'active':
        return t('ActiveAdvertisements');
      case 'rejected':
        return t('RejectedAdvertisements');
      default:
        return status;
    }
  };

  const handleApprove = async (advertisementId) => {
    const targetId = Number(advertisementId ?? selectedAdvertisement?.id);
    if (!targetId) {
      alert(t('SelectAdvertisementFirst', 'يرجى اختيار إعلان أولاً'));
      return;
    }

    try {
      setActionLoading(true);
      const response = await advertisementService.approveAdvertisement(targetId);
      
      if (response.success) {
        await loadAdvertisements();
        setSelectedAdvertisement(null);
        alert(t('AdvertisementApprovedSuccessfully'));
      } else {
        console.error('Failed to approve advertisement:', response.message);
      }
    } catch (error) {
      console.error('Error approving advertisement:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (advertisementId) => {
    const targetId = Number(advertisementId ?? selectedAdvertisement?.id);
    if (!targetId) {
      alert(t('SelectAdvertisementFirst', 'يرجى اختيار إعلان أولاً'));
      return;
    }

    if (!rejectionReason.trim()) {
      alert(t('PleaseEnterRejectionReason'));
      return;
    }

    try {
      setActionLoading(true);
      const response = await advertisementService.rejectAdvertisement(
        targetId,
        rejectionReason
      );
      
      if (response.success) {
        await loadAdvertisements();
        setRejectDialog(false);
        setSelectedAdvertisement(null);
        setRejectionReason('');
        alert(t('AdvertisementRejectedSuccessfully'));
      } else {
        console.error('Failed to reject advertisement:', response.message);
      }
    } catch (error) {
      console.error('Error rejecting advertisement:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddDesign = async (advertisementId) => {
    const targetId = Number(advertisementId ?? selectedAdvertisement?.id);
    if (!targetId) {
      alert(t('SelectAdvertisementFirst', 'يرجى اختيار إعلان أولاً'));
      return;
    }

    if (!designForm.design) {
      alert(t('PleaseUploadDesignFile'));
      return;
    }

    try {
      setActionLoading(true);
      const formData = new FormData();
      formData.append('advertisement_id', targetId);
      formData.append('design', designForm.design);
      formData.append('design_name', designForm.design_name);
      formData.append('design_description', designForm.design_description);
      formData.append('design_cost', designForm.design_cost);

      const response = await advertisementService.addDesign(formData);
      
      if (response.success) {
        await loadAdvertisements();
        setDesignDialog(false);
        setSelectedAdvertisement(null);
        setDesignForm({
          design_name: '',
          design_description: '',
          design_cost: 0,
          design: null,
        });
        alert(t('DesignAddedSuccessfully'));
      } else {
        console.error('Failed to add design:', response.message);
      }
    } catch (error) {
      console.error('Error adding design:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateAdvertisement = async () => {
    if (!advertisementForm.name.trim()) {
      alert(t('PleaseEnterAdvertisementName'));
      return;
    }
    if (!advertisementForm.description.trim()) {
      alert(t('PleaseEnterAdvertisementDescription'));
      return;
    }
    if (!advertisementForm.user_id) {
      alert(t('PleaseSelectServiceProvider'));
      return;
    }
    if (!advertisementForm.image) {
      alert(t('PleaseUploadAdvertisementImage'));
      return;
    }

    try {
      setActionLoading(true);
      const formData = new FormData();
      formData.append('name', advertisementForm.name);
      formData.append('description', advertisementForm.description);
      formData.append('type', advertisementForm.type);
      formData.append('image', advertisementForm.image);
      formData.append('user_id', advertisementForm.user_id);
      if (advertisementForm.type === 'custom_design') {
        formData.append('design_requirements', advertisementForm.design_requirements);
      }

      const response = await advertisementService.createAdvertisement(formData);
      
      if (response.success) {
        await loadAdvertisements();
        setCreateDialog(false);
        setAdvertisementForm({
          name: '',
          description: '',
          type: 'ready_image',
          image: null,
          design_requirements: '',
          user_id: '',
        });
        alert(t('AdvertisementCreatedSuccessfully'));
      } else {
        console.error('Failed to create advertisement:', response.message);
      }
    } catch (error) {
      console.error('Error creating advertisement:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditAdvertisement = async () => {
    if (!advertisementForm.name.trim()) {
      alert(t('PleaseEnterAdvertisementName'));
      return;
    }
    if (!advertisementForm.description.trim()) {
      alert(t('PleaseEnterAdvertisementDescription'));
      return;
    }

    try {
      setActionLoading(true);
      const formData = new FormData();
      formData.append('name', advertisementForm.name);
      formData.append('description', advertisementForm.description);
      formData.append('type', advertisementForm.type);
      if (advertisementForm.image) {
        formData.append('image', advertisementForm.image);
      }
      if (advertisementForm.type === 'custom_design') {
        formData.append('design_requirements', advertisementForm.design_requirements);
      }

      const response = await advertisementService.updateAdvertisement(
        Number(selectedAdvertisement?.id),
        formData
      );
      
      if (response.success) {
        await loadAdvertisements();
        alert(t('AdvertisementUpdatedSuccessfully'));
      } else {
        console.error('Failed to update advertisement:', response.message);
      }
      setEditDialog(false);
      setSelectedAdvertisement(null);
      setAdvertisementForm({
        name: '',
        description: '',
        type: 'ready_image',
        image: null,
        design_requirements: '',
        user_id: '',
      });
    } catch (error) {
      console.error('Error updating advertisement:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAdvertisement = async (advertisement) => {
    if (window.confirm('Are you sure you want to delete this advertisement?')) {
      try {
        setActionLoading(true);
        const response = await advertisementService.deleteAdvertisement(advertisement.id);
        
        if (response.success) {
          await loadAdvertisements();
          alert(t('AdvertisementDeletedSuccessfully'));
        } else {
          console.error('Failed to delete advertisement:', response.message);
        }
      } catch (error) {
        console.error('Error deleting advertisement:', error);
      } finally {
        setActionLoading(false);
      }
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setDesignForm(prev => ({ ...prev, design: file }));
  };

  const handleAdvertisementImageChange = (event) => {
    const file = event.target.files[0];
    setAdvertisementForm(prev => ({ ...prev, image: file }));
  };

  const openEditDialog = (advertisement) => {
    setSelectedAdvertisement(advertisement);
    setAdvertisementForm({
      name: advertisement.name,
      description: advertisement.description,
      type: advertisement.type,
      image: null,
      design_requirements: advertisement.design_requirements || '',
      user_id: advertisement.user_id,
    });
    setEditDialog(true);
  };

  const renderAdvertisementCard = (advertisement) => (
    <div key={advertisement.id} className="bg-white rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 p-6 mb-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 mr-4">
          <div className="flex items-center gap-3 mb-3">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(advertisement.status)}`}>
              {getStatusLabel(advertisement.status)}
            </span>
            <span className="text-xs text-gray-500">
              {new Date(advertisement.created_at).toLocaleDateString('en-US')}
            </span>
          </div>
          
          <h3 className="text-xl font-bold text-gray-900 mb-2 leading-tight">
            {advertisement.name}
          </h3>
          
          <p className="text-gray-600 mb-3 leading-relaxed">
            {advertisement.description}
          </p>
          
          <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span>بواسطة: {advertisement.user?.first_name} {advertisement.user?.last_name}</span>
            </div>
          </div>

          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-1 text-blue-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span className="font-medium">{t('Views')}: {advertisement.views_count || 0}</span>
            </div>
            <div className="flex items-center gap-1 text-green-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
              <span className="font-medium">{t('Clicks')}: {advertisement.clicks_count || 0}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end gap-3">
          {advertisement.image_url && (
            <div className="w-20 h-20 rounded-lg overflow-hidden border-2 border-gray-200 shadow-sm">
              <img 
                src={advertisement.image_url} 
                alt={advertisement.name} 
                className="w-full h-full object-cover cursor-pointer transition-transform duration-200 hover:scale-105"
                onClick={() => {
                  setSelectedAdvertisement(advertisement);
                  window.dispatchEvent(new CustomEvent('open-ad-preview', { detail: advertisement }));
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
          
          <div className="flex gap-2">
            <button
              onClick={() => openEditDialog(advertisement)}
              className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {t('Edit')}
            </button>
            <button
              onClick={() => handleDeleteAdvertisement(advertisement)}
              className="inline-flex items-center gap-1 px-3 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-6h.01M9 7h6m-6 0V5a2 2 0 012-2h2a2 2 0 012 2v2" />
              </svg>
              {t('Delete')}
            </button>
          </div>
        </div>
      </div>

      {advertisement.type === 'custom_design' && (
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-1">
            <strong>{t('DesignRequirements')}:</strong>
          </p>
          <p className="text-sm text-gray-600">
            {advertisement.design_requirements}
          </p>
        </div>
      )}

      {advertisement.rejection_reason && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">
            <strong>{t('RejectionReason')}:</strong> {advertisement.rejection_reason}
          </p>
        </div>
      )}

      {advertisement.package && (
        <div className="mb-2">
          <p className="text-sm text-gray-700">
            <strong>Package:</strong> {advertisement.package.name_ar}
          </p>
        </div>
      )}

      {advertisement.total_cost && (
        <div className="mb-2">
          <p className="text-sm text-gray-700">
            <strong>Total Cost:</strong> ${advertisement.total_cost}
          </p>
        </div>
      )}

      <div className="flex gap-4 mt-4">
        <span className="text-sm text-gray-500">
          {t('Views')}: {advertisement.views_count || 0}
        </span>
        <span className="text-sm text-gray-500">
          {t('Clicks')}: {advertisement.clicks_count || 0}
        </span>
      </div>

      {/* Action Buttons */}
      {advertisement.status === 'pending' && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleApprove(advertisement.id)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('ApproveAdvertisement')}
            </button>
            <button
              onClick={() => {
                setSelectedAdvertisement(advertisement);
                setRejectDialog(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              {t('RejectAdvertisement')}
            </button>
            {advertisement.type === 'custom_design' && (
              <button
                onClick={() => {
                  setSelectedAdvertisement(advertisement);
                  setDesignDialog(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
                </svg>
                {t('AddDesign')}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3">{t('Loading')}</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('AdvertisementManagement')}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                إدارة الإعلانات والتحكم في المحتوى
              </p>
            </div>
            <button
              onClick={() => setCreateDialog(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t('CreateAdvertisement')}
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">

        {/* Tabs */}
        <div className="mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1">
            <nav className="flex space-x-1">
              {tabs.map((tab, index) => (
                <button
                  key={`tab-${tab.value}-${index}`}
                  onClick={() => setSelectedTab(index)}
                  className={`flex-1 py-3 px-4 text-sm font-medium rounded-md transition-all duration-200 ${
                    selectedTab === index
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

      {/* Advertisements List */}
      <div className="space-y-4">
        {getFilteredAdvertisements().map(renderAdvertisementCard)}
      </div>

        {getFilteredAdvertisements().length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('NoAdvertisementsFound')}
            </h3>
            <p className="text-gray-600 mb-6">
              لا توجد إعلانات في هذه الفئة حالياً
            </p>
            <button
              onClick={() => setCreateDialog(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {t('CreateAdvertisement')}
            </button>
          </div>
        )}
      </div>

      {/* Create Advertisement Dialog */}
      {createDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">{t('CreateAdvertisement')}</h3>
                <button
                  onClick={() => setCreateDialog(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label htmlFor="create-name" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('AdvertisementName')}
                  </label>
                  <input
                    id="create-name"
                    type="text"
                    value={advertisementForm.name}
                    onChange={(e) => setAdvertisementForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="create-description" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('AdvertisementDescription')}
                  </label>
                  <textarea
                    id="create-description"
                    value={advertisementForm.description}
                    onChange={(e) => setAdvertisementForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="create-type" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('AdvertisementType')}
                  </label>
                  <select
                    id="create-type"
                    value={advertisementForm.type}
                    onChange={(e) => setAdvertisementForm(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="ready_image">{t('ReadyImage')}</option>
                    <option value="custom_design">{t('CustomDesign')}</option>
                  </select>
                </div>
                {advertisementForm.type === 'custom_design' && (
                  <div>
                    <label htmlFor="create-requirements" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('DesignRequirements')}
                    </label>
                    <textarea
                      id="create-requirements"
                      value={advertisementForm.design_requirements}
                      onChange={(e) => setAdvertisementForm(prev => ({ ...prev, design_requirements: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
                <div>
                  <label htmlFor="create-provider" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('SelectServiceProvider')}
                  </label>
                  <select
                    id="create-provider"
                    value={advertisementForm.user_id}
                    onChange={(e) => setAdvertisementForm(prev => ({ ...prev, user_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('SelectServiceProvider')}</option>
                    {serviceProviders.map(provider => (
                      <option key={provider.id} value={provider.id}>
                        {provider.first_name} {provider.last_name} ({provider.email})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="create-image" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('AdvertisementImage')}
                  </label>
                  <input
                    id="create-image"
                    type="file"
                    accept="image/*"
                    onChange={handleAdvertisementImageChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setCreateDialog(false)}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {t('Cancel')}
                </button>
                <button
                  onClick={handleCreateAdvertisement}
                  disabled={actionLoading}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {actionLoading ? (
                    <>
                      <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('Loading')}
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      {t('Create')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Advertisement Dialog */}
      {editDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('EditAdvertisement')}</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('AdvertisementName')}
                  </label>
                  <input
                    id="edit-name"
                    type="text"
                    value={advertisementForm.name}
                    onChange={(e) => setAdvertisementForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('AdvertisementDescription')}
                  </label>
                  <textarea
                    id="edit-description"
                    value={advertisementForm.description}
                    onChange={(e) => setAdvertisementForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="edit-type" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('AdvertisementType')}
                  </label>
                  <select
                    id="edit-type"
                    value={advertisementForm.type}
                    onChange={(e) => setAdvertisementForm(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="ready_image">{t('ReadyImage')}</option>
                    <option value="custom_design">{t('CustomDesign')}</option>
                  </select>
                </div>
                {advertisementForm.type === 'custom_design' && (
                  <div>
                    <label htmlFor="edit-requirements" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('DesignRequirements')}
                    </label>
                    <textarea
                      id="edit-requirements"
                      value={advertisementForm.design_requirements}
                      onChange={(e) => setAdvertisementForm(prev => ({ ...prev, design_requirements: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
                <div>
                  <label htmlFor="edit-image" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('AdvertisementImage')} (اختياري)
                  </label>
                  {selectedAdvertisement?.image_url && (
                    <div className="mb-3">
                      <p className="text-sm text-gray-600 mb-2">الصورة الحالية:</p>
                      <img 
                        src={selectedAdvertisement.image_url} 
                        alt="Current advertisement" 
                        className="w-32 h-32 object-cover rounded-md border"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <input
                    id="edit-image"
                    type="file"
                    accept="image/*"
                    onChange={handleAdvertisementImageChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {advertisementForm.image && (
                    <p className="mt-1 text-sm text-gray-600">
                      تم اختيار صورة جديدة: {advertisementForm.image.name}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setEditDialog(false)}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  {t('Cancel')}
                </button>
                <button
                  onClick={handleEditAdvertisement}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? t('Loading') : t('Save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Dialog */}
      {rejectDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('RejectAdvertisement')}</h3>
              <div className="mb-4">
                <label htmlFor="rejection-reason" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('RejectionReason')}
                </label>
                <textarea
                  id="rejection-reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('PleaseEnterRejectionReason')}
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setRejectDialog(false)}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  {t('Cancel')}
                </button>
                <button
                  onClick={() => handleReject(selectedAdvertisement?.id)}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? t('Loading') : t('RejectAdvertisement')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Design Dialog */}
      {designDialog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('AddDesign')}</h3>
              <div className="space-y-4">
                <div>
                  <label htmlFor="design-name" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('DesignName')}
                  </label>
                  <input
                    id="design-name"
                    type="text"
                    value={designForm.design_name}
                    onChange={(e) => setDesignForm(prev => ({ ...prev, design_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="design-description" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('DesignDescription')}
                  </label>
                  <textarea
                    id="design-description"
                    value={designForm.design_description}
                    onChange={(e) => setDesignForm(prev => ({ ...prev, design_description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="design-cost" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('DesignCost')}
                  </label>
                  <input
                    id="design-cost"
                    type="number"
                    value={designForm.design_cost}
                    onChange={(e) => setDesignForm(prev => ({ ...prev, design_cost: Number.parseFloat(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label htmlFor="design-file" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('UploadDesign')}
                  </label>
                  <input
                    id="design-file"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {designForm.design && (
                    <p className="mt-1 text-sm text-gray-600">
                      Selected: {designForm.design.name}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setDesignDialog(false)}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  {t('Cancel')}
                </button>
                <button
                  onClick={() => handleAddDesign(selectedAdvertisement?.id)}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {actionLoading ? t('Loading') : t('AddDesign')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {selectedAdvertisement && selectedAdvertisement.image_url && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setSelectedAdvertisement(null)}
        >
          <div
            className="relative max-w-4xl max-h-[90vh] w-full mx-4 bg-white rounded-xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedAdvertisement(null)}
              className="absolute top-3 right-3 inline-flex items-center justify-center w-10 h-10 rounded-full bg-black/60 text-white hover:bg-black transition-colors"
              aria-label="Close preview"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="p-6 bg-gray-50 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">{selectedAdvertisement.name}</h3>
              <p className="mt-1 text-sm text-gray-600">{selectedAdvertisement.description}</p>
            </div>
            <div className="p-4 bg-gray-900">
              <img
                src={selectedAdvertisement.image_url}
                alt={selectedAdvertisement.name}
                className="mx-auto max-h-[70vh] w-auto object-contain rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvertisementsManagement;